import { createClient, AnamEvent } from '@anam-ai/js-sdk';

// Registry to track all active AnamService instances
const activeServices = new Set();

/**
 * Close all active ANAM sessions
 * @returns {Promise<void>}
 */
export const closeAllAnamSessions = async () => {
  console.log(`[ANAM REGISTRY] Closing ${activeServices.size} active session(s)...`);
  const disconnectPromises = Array.from(activeServices).map(async (service) => {
    try {
      if (service && typeof service.disconnect === 'function') {
        await service.disconnect();
      }
    } catch (error) {
      console.error('[ANAM REGISTRY] Error disconnecting service:', error);
    }
  });
  
  await Promise.all(disconnectPromises);
  activeServices.clear();
  console.log('[ANAM REGISTRY] All sessions closed');
};

export class AnamService {
  constructor(callbacks = {}) {
    this.client = null;
    this.sessionToken = '';
    this.isReady = false;
    this.isStreaming = false;
    this.audioReady = false;
    this.callbacks = callbacks;
    this.answerResolve = null;
    this.currentQuestion = null;
    this.isSpeaking = false;
    this.readyForAnswer = false;
    this.speechEndTime = 0;
    
    // Register this service instance
    activeServices.add(this);
  }

  async initialize(sessionToken, videoElement) {
    if (this.client && this.sessionToken === sessionToken && this.isReady) {
      console.log('[ANAM SERVICE] Already initialized with same token');
      return;
    }

    this.sessionToken = sessionToken;

    try {
      console.log('[ANAM SERVICE] Initializing Anam client...');
      this.client = createClient(sessionToken);
      this.setupEventListeners();

      const videoId = `anam-video-${Date.now()}`;
      videoElement.id = videoId;
      
      console.log('[ANAM SERVICE] Starting stream to video element:', videoId);
      await this.client.streamToVideoElement(videoId);
      console.log('[ANAM SERVICE] Stream started, waiting for ready state...');
    } catch (error) {
      console.error('[ANAM SERVICE] Failed to initialize:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize Anam';
      if (this.callbacks.onError) {
        this.callbacks.onError(new Error(errorMessage));
      }
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.client) return;

    this.client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
      console.log('[ANAM SERVICE] Connection established');
      this.isReady = true;
    });

    this.client.addListener(AnamEvent.SESSION_READY, () => {
      console.log('[ANAM SERVICE] Session ready');
      this.isReady = true;
    });

    this.client.addListener(AnamEvent.VIDEO_STREAM_STARTED, () => {
      console.log('[ANAM SERVICE] Video stream started');
      this.isStreaming = true;
      setTimeout(() => {
        if (this.callbacks.onReady) {
          this.callbacks.onReady();
        }
      }, 500);
    });

    this.client.addListener(AnamEvent.AUDIO_STREAM_STARTED, () => {
      console.log('[ANAM SERVICE] Audio stream started');
      this.audioReady = true;
    });

    this.client.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (event) => {
      console.log('[ANAM SERVICE] Message stream event:', event);
      
      // Handle persona speech end
      if (event && event.role === 'persona' && event.endOfSpeech === true) {
        console.log('[ANAM SERVICE] Persona finished speaking');
        this.isSpeaking = false;
        this.speechEndTime = Date.now();
        // Wait a bit before accepting answers to ensure avatar audio has stopped
        setTimeout(() => {
          this.readyForAnswer = true;
          console.log('[ANAM SERVICE] Ready to accept answers');
        }, 800); // Wait 800ms after speech ends before accepting answers
        if (this.callbacks.onSpeechEnd) {
          this.callbacks.onSpeechEnd();
        }
      }

      // Handle persona speech start - stop microphone immediately
      if (event && event.role === 'persona' && event.content) {
        if (!this.isSpeaking) {
          console.log('[ANAM SERVICE] Persona started speaking - ensuring microphone is stopped');
          this.isSpeaking = true;
          if (this.callbacks.onSpeechStart) {
            this.callbacks.onSpeechStart();
          }
        }
      }

      // Handle user messages - ONLY accept if avatar is NOT speaking and we're ready for answers
      if (event && event.role === 'user' && event.endOfSpeech === true && event.content) {
        const userMessage = event.content.trim();
        // Ignore messages received while avatar is speaking or before we're ready
        if (this.isSpeaking) {
          console.log('[ANAM SERVICE] Ignoring user message - avatar is still speaking:', userMessage);
          return;
        }
        if (!this.readyForAnswer) {
          console.log('[ANAM SERVICE] Ignoring user message - not ready for answers yet:', userMessage);
          return;
        }
        // Only accept messages that came after speech ended (with some buffer)
        const messageTime = Date.now();
        if (this.speechEndTime > 0 && messageTime < this.speechEndTime + 500) {
          console.log('[ANAM SERVICE] Ignoring user message - received too soon after speech ended:', userMessage);
          return;
        }
        if (userMessage && this.answerResolve) {
          console.log('[ANAM SERVICE] User answer received:', userMessage);
          const resolve = this.answerResolve;
          this.answerResolve = null;
          this.readyForAnswer = false; // Reset flag
          resolve(userMessage);
        }
      }
    });

    this.client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, (messages) => {
      // Ignore message history updates while avatar is speaking
      if (this.isSpeaking) {
        console.log('[ANAM SERVICE] Ignoring message history - avatar is speaking');
        return;
      }
      if (!this.readyForAnswer) {
        console.log('[ANAM SERVICE] Ignoring message history - not ready for answers');
        return;
      }
      const userMessages = messages.filter(msg => msg.role === 'user');
      if (userMessages.length > 0 && this.answerResolve) {
        const latestUserMessage = userMessages[userMessages.length - 1];
        if (latestUserMessage.content) {
          console.log('[ANAM SERVICE] User answer from history:', latestUserMessage.content);
          const resolve = this.answerResolve;
          this.answerResolve = null;
          this.readyForAnswer = false; // Reset flag
          resolve(latestUserMessage.content);
        }
      }
    });

    this.client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
      console.log('[ANAM SERVICE] Connection closed');
      this.isReady = false;
      this.isStreaming = false;
      this.audioReady = false;
    });
  }

  async askQuestion(question, onMicrophoneStop) {
    if (!this.client) {
      throw new Error('Anam service not initialized');
    }

    if (!this.isStreaming) {
      throw new Error('Video stream not started');
    }

    if (!this.audioReady) {
      throw new Error('Audio stream not ready');
    }

    if (!question || question.trim().length === 0) {
      throw new Error('Question cannot be empty');
    }

    // Stop microphone BEFORE sending question to prevent picking up avatar's voice
    if (onMicrophoneStop) {
      console.log('[ANAM SERVICE] Stopping microphone before avatar speaks...');
      onMicrophoneStop();
    }

    // Reset any pending answer
    if (this.answerResolve) {
      console.warn('[ANAM SERVICE] Previous answer pending, resolving with empty string');
      const resolve = this.answerResolve;
      this.answerResolve = null;
      resolve('');
    }

    this.currentQuestion = question.trim();
    this.isSpeaking = true;
    this.readyForAnswer = false; // Not ready for answers while speaking

    // Notify that speech is starting
    if (this.callbacks.onSpeechStart) {
      this.callbacks.onSpeechStart();
    }

    try {
      console.log('[ANAM SERVICE] Asking question:', this.currentQuestion);
      
      // Send question to Anam
      await this.client.talk(this.currentQuestion);
      
      console.log('[ANAM SERVICE] Question sent, waiting for answer...');

      // Wait for answer
      return new Promise((resolve, reject) => {
        this.answerResolve = resolve;

        // Timeout after 60 seconds
        const timeout = setTimeout(() => {
          if (this.answerResolve === resolve) {
            this.answerResolve = null;
            reject(new Error('Timeout waiting for answer'));
          }
        }, 60000);

        // Clear timeout when resolved
        const originalResolve = resolve;
        this.answerResolve = (answer) => {
          clearTimeout(timeout);
          originalResolve(answer);
        };
      });
    } catch (error) {
      this.isSpeaking = false;
      this.answerResolve = null;
      console.error('[ANAM SERVICE] Error asking question:', error);
      throw error;
    }
  }

  async disconnect() {
    // Unregister this service instance
    activeServices.delete(this);
    
    if (this.client) {
      try {
        console.log('[ANAM SERVICE] Disconnecting...');
        await this.client.stopStreaming();
        this.client = null;
        this.isReady = false;
        this.isStreaming = false;
        this.audioReady = false;
        this.isSpeaking = false;
        this.readyForAnswer = false;
        this.speechEndTime = 0;
        this.answerResolve = null;
        this.currentQuestion = null;
      } catch (error) {
        console.error('[ANAM SERVICE] Error disconnecting:', error);
        throw error;
      }
    }
  }

  isConnected() {
    return this.isStreaming && this.isReady && this.audioReady;
  }

  isCurrentlySpeaking() {
    return this.isSpeaking;
  }

  async sendMessage(message, onMicrophoneStop) {
    // Send a message without waiting for an answer (useful for completion messages)
    if (!this.client) {
      throw new Error('Anam service not initialized');
    }

    if (!this.isStreaming) {
      throw new Error('Video stream not started');
    }

    if (!this.audioReady) {
      throw new Error('Audio stream not ready');
    }

    if (!message || message.trim().length === 0) {
      throw new Error('Message cannot be empty');
    }

    // Stop microphone before sending message
    if (onMicrophoneStop) {
      console.log('[ANAM SERVICE] Stopping microphone before sending message...');
      onMicrophoneStop();
    }

    this.currentQuestion = message.trim();
    this.isSpeaking = true;
    this.readyForAnswer = false;

    // Notify that speech is starting
    if (this.callbacks.onSpeechStart) {
      this.callbacks.onSpeechStart();
    }

    try {
      console.log('[ANAM SERVICE] Sending message:', this.currentQuestion);
      await this.client.talk(this.currentQuestion);
      console.log('[ANAM SERVICE] Message sent successfully');
    } catch (error) {
      this.isSpeaking = false;
      console.error('[ANAM SERVICE] Error sending message:', error);
      throw error;
    }
  }
}

